import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Sparkles, Heart, Users } from "lucide-react";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

const pillars = [
  {
    icon: ShieldCheck,
    title: "100% Authentic",
    description: "Every product is sourced directly from authorized distributors. No fakes, no compromises.",
  },
  {
    icon: Sparkles,
    title: "Curated Selection",
    description: "We handpick products that work—formulas proven to deliver real results for real skin concerns.",
  },
  {
    icon: Heart,
    title: "Expert Guidance",
    description: "Get personalized skincare advice. We help you build routines that actually work for your skin.",
  },
  {
    icon: Users,
    title: "Trusted Community",
    description: "Join hundreds of happy customers who've transformed their skin with our guidance.",
  },
];

export function WhyChooseUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isIOSPortrait = useIOSPortrait();

  // iOS Portrait: Render completely static version to prevent Framer Motion transforms
  if (isIOSPortrait) {
    return (
      <section ref={ref} className="section-padding bg-gradient-primary relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-card/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-card/30 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header - Static */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-foreground/60 mb-4">
              Why Us
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
              Why Choose <span className="text-gradient">Nurture</span>?
            </h2>
            <p className="text-muted-foreground text-lg">
              We're more than a skincare store—we're your partners in achieving 
              healthy, radiant skin.
            </p>
          </div>

          {/* Pillars Grid - Static */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar) => (
              <div key={pillar.title} className="text-center group">
                <div className="bg-card rounded-2xl p-8 shadow-card h-full">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-rose text-primary-foreground mb-6">
                    <pillar.icon className="h-8 w-8" />
                  </div>
                  {/* Content */}
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                    {pillar.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="section-padding bg-gradient-primary relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-card/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-card/30 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-foreground/60 mb-4">
            Why Us
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
            Why Choose <span className="text-gradient">Nurture</span>?
          </h2>
          <p className="text-muted-foreground text-lg">
            We're more than a skincare store—we're your partners in achieving 
            healthy, radiant skin.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center group"
            >
              <div className="bg-card rounded-2xl p-8 shadow-card hover-lift h-full">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-rose text-primary-foreground mb-6 group-hover:animate-pulse-glow transition-all">
                  <pillar.icon className="h-8 w-8" />
                </div>
                {/* Content */}
                <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                  {pillar.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
